import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { SectionCard } from './SectionCard';
import ImageUpload from '../ImageUpload';
import IconPicker from '../IconPicker';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';

export default function SectionsManager({ onSaved, toast }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/admin/sections'); setData(r.data || {}); } catch { /* ignore */ }
    })();
  }, []);

  if (!data) return <SectionCard title="Site Sections"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7941e]" /></SectionCard>;

  const save = async () => {
    try {
      const payload = { ...data };
      delete payload._id; delete payload._singleton;
      payload.nav_items = (payload.nav_items || []).filter(n => n.label && n.path);
      payload.ticker_items = (payload.ticker_items || []).filter(Boolean);
      payload.stats_counters = (payload.stats_counters || []).filter(s => s.label);
      payload.whychoose_features = (payload.whychoose_features || []).filter(f => f.title);
      payload.manufacturing_steps = (payload.manufacturing_steps || []).filter(s => s.title);
      payload.certifications_items = (payload.certifications_items || []).filter(c => c.title);
      await api.put('/admin/sections', payload);
      toast({ title: 'Saved', description: 'Site sections updated' });
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const updateList = (key, idx, field, value) => {
    const list = [...(data[key] || [])];
    list[idx] = { ...list[idx], [field]: value };
    setData({ ...data, [key]: list });
  };
  const addItem = (key, item) => setData({ ...data, [key]: [...(data[key] || []), item] });
  const removeItem = (key, idx) => setData({ ...data, [key]: data[key].filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3 sticky top-16 z-20">
        <h2 className="font-display uppercase tracking-wide">Site Sections & Theme</h2>
        <button data-testid="save-sections-btn" onClick={save} className="btn-brand text-sm"><Save className="w-4 h-4" /> Save All Changes</button>
      </div>

      {/* Section visibility toggles */}
      <SectionCard title="Section Visibility" subtitle="Show or hide entire sections on home page">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {[
            ['show_h1_band', 'H1 Heading Band'],
            ['show_ticker', 'Ticker Bar'],
            ['show_welcome', 'Welcome Section'],
            ['show_stats', 'Stats Counters'],
            ['show_certifications', 'Certifications'],
            ['show_manufacturing', 'Manufacturing Process'],
            ['show_spotlight', 'Product Spotlight'],
            ['show_whychoose', 'Why Choose Us'],
            ['show_products', 'Products Grid'],
            ['show_testimonials', 'Testimonials'],
            ['show_cta', 'CTA Banner'],
            ['show_industries', 'Industries'],
            ['show_gallery_preview', 'Gallery Preview'],
          ].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-2 text-sm border border-gray-200 rounded px-3 py-2 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={data[k] !== false} onChange={e => setData({ ...data, [k]: e.target.checked })} />
              {label}
            </label>
          ))}
        </div>
      </SectionCard>

      {/* Header */}
      <SectionCard title="Header & Top Bar">
        <div className="space-y-3">
          <ImageUpload label="Header Logo" value={data.header_logo_url} onChange={v => setData({ ...data, header_logo_url: v })} />
          <div><Label>Top Bar Welcome Text</Label><Input value={data.topbar_welcome || ''} onChange={e => setData({ ...data, topbar_welcome: e.target.value })} /></div>
          <div>
            <Label>Navigation Menu Items</Label>
            <div className="space-y-2 mt-1">
              {(data.nav_items || []).map((n, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <Input placeholder="Label (e.g. Home)" value={n.label} onChange={e => updateList('nav_items', i, 'label', e.target.value)} />
                  <Input placeholder="Path (e.g. /)" value={n.path} onChange={e => updateList('nav_items', i, 'path', e.target.value)} />
                  <button onClick={() => removeItem('nav_items', i)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => addItem('nav_items', { label: '', path: '/' })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"><Plus className="w-4 h-4" /> Add menu item</button>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Ticker */}
      <SectionCard title="Ticker Bar Items">
        <div>
          <Label>Rolling text items (one per line)</Label>
          <Textarea rows={5} value={(data.ticker_items || []).join('\n')} onChange={e => setData({ ...data, ticker_items: e.target.value.split('\n') })} />
        </div>
      </SectionCard>

      {/* Stats Counters */}
      <SectionCard title="Stats Counters">
        <div className="space-y-3">
          {(data.stats_counters || []).map((st, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 grid sm:grid-cols-[100px_1fr_100px_70px_auto] gap-2 items-end">
              <IconPicker label="Icon" value={st.icon} onChange={v => updateList('stats_counters', i, 'icon', v)} />
              <div><Label>Label</Label><Input value={st.label} onChange={e => updateList('stats_counters', i, 'label', e.target.value)} /></div>
              <div><Label>Value</Label><Input type="number" value={st.value} onChange={e => updateList('stats_counters', i, 'value', parseInt(e.target.value) || 0)} /></div>
              <div><Label>Suffix</Label><Input value={st.suffix} onChange={e => updateList('stats_counters', i, 'suffix', e.target.value)} /></div>
              <button onClick={() => removeItem('stats_counters', i)} className="p-2 text-red-600 hover:bg-red-50 rounded h-10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => addItem('stats_counters', { icon: 'Award', label: '', value: 0, suffix: '+' })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"><Plus className="w-4 h-4" /> Add counter</button>
        </div>
      </SectionCard>

      {/* Why Choose */}
      <SectionCard title="Why Choose Us">
        <div className="space-y-3">
          <div><Label>Section Title</Label><Input value={data.whychoose_title || ''} onChange={e => setData({ ...data, whychoose_title: e.target.value })} /></div>
          {(data.whychoose_features || []).map((f, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 grid sm:grid-cols-[120px_1fr_auto] gap-2 items-end">
              <IconPicker label="Icon" value={f.icon} onChange={v => updateList('whychoose_features', i, 'icon', v)} />
              <div><Label>Title</Label><Input value={f.title} onChange={e => updateList('whychoose_features', i, 'title', e.target.value)} /></div>
              <button onClick={() => removeItem('whychoose_features', i)} className="p-2 text-red-600 hover:bg-red-50 rounded h-10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => addItem('whychoose_features', { icon: 'CheckCircle', title: '' })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"><Plus className="w-4 h-4" /> Add feature</button>
        </div>
      </SectionCard>

      {/* Manufacturing */}
      <SectionCard title="Manufacturing Process Steps">
        <div className="space-y-3">
          <div><Label>Section Title</Label><Input value={data.manufacturing_title || ''} onChange={e => setData({ ...data, manufacturing_title: e.target.value })} /></div>
          {(data.manufacturing_steps || []).map((st, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 space-y-2">
              <div className="grid sm:grid-cols-[120px_80px_1fr_auto] gap-2 items-end">
                <IconPicker label="Icon" value={st.icon} onChange={v => updateList('manufacturing_steps', i, 'icon', v)} />
                <div><Label>Step #</Label><Input value={st.number} onChange={e => updateList('manufacturing_steps', i, 'number', e.target.value)} /></div>
                <div><Label>Title</Label><Input value={st.title} onChange={e => updateList('manufacturing_steps', i, 'title', e.target.value)} /></div>
                <button onClick={() => removeItem('manufacturing_steps', i)} className="p-2 text-red-600 hover:bg-red-50 rounded h-10"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div><Label>Description</Label><Input value={st.description || ''} onChange={e => updateList('manufacturing_steps', i, 'description', e.target.value)} /></div>
            </div>
          ))}
          <button onClick={() => addItem('manufacturing_steps', { icon: 'Cog', number: '', title: '', description: '' })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"><Plus className="w-4 h-4" /> Add step</button>
        </div>
      </SectionCard>

      {/* Certifications */}
      <SectionCard title="Certifications Showcase">
        <div className="space-y-3">
          <div><Label>Section Title</Label><Input value={data.certifications_title || ''} onChange={e => setData({ ...data, certifications_title: e.target.value })} /></div>
          {(data.certifications_items || []).map((c, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 grid sm:grid-cols-[120px_1fr_1fr_auto] gap-2 items-end">
              <IconPicker label="Icon" value={c.icon} onChange={v => updateList('certifications_items', i, 'icon', v)} />
              <div><Label>Title</Label><Input value={c.title} onChange={e => updateList('certifications_items', i, 'title', e.target.value)} /></div>
              <div><Label>Subtitle</Label><Input value={c.subtitle || ''} onChange={e => updateList('certifications_items', i, 'subtitle', e.target.value)} /></div>
              <button onClick={() => removeItem('certifications_items', i)} className="p-2 text-red-600 hover:bg-red-50 rounded h-10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => addItem('certifications_items', { icon: 'Award', title: '', subtitle: '' })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"><Plus className="w-4 h-4" /> Add certification</button>
        </div>
      </SectionCard>

      {/* Industries title */}
      <SectionCard title="Industries Section">
        <div><Label>Section Title</Label><Input value={data.industries_title || ''} onChange={e => setData({ ...data, industries_title: e.target.value })} /></div>
        <p className="text-xs text-gray-500 mt-2">(Edit the industries list in <strong>Company Info</strong> tab)</p>
      </SectionCard>

      {/* Theme */}
      <SectionCard title="Theme Colors" subtitle="Hex codes (preview won't update CSS until rebuild)">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['color_primary', 'Primary (Orange)'],
            ['color_secondary', 'Secondary (Dark Orange)'],
            ['color_dark', 'Dark (Black)'],
            ['color_red', 'Red Accent'],
          ].map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <div className="flex gap-2 items-center mt-1">
                <input type="color" value={data[k] || '#000'} onChange={e => setData({ ...data, [k]: e.target.value })} className="w-12 h-9 border border-gray-200 rounded cursor-pointer" />
                <Input value={data[k] || ''} onChange={e => setData({ ...data, [k]: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* SEO */}
      <SectionCard title="SEO — Per Page" subtitle="Meta title & description for search engines and social previews">
        <div className="space-y-3">
          {[
            ['seo_home', 'Home'],
            ['seo_about', 'About'],
            ['seo_products', 'Products'],
            ['seo_gallery', 'Gallery'],
            ['seo_contact', 'Contact'],
          ].map(([k, label]) => (
            <div key={k} className="border border-gray-200 rounded p-3 space-y-2">
              <p className="font-display text-xs uppercase tracking-[3px] text-[#f7941e]">{label}</p>
              <div><Label>Meta Title</Label><Input value={data[`${k}_title`] || ''} onChange={e => setData({ ...data, [`${k}_title`]: e.target.value })} /></div>
              <div><Label>Meta Description</Label><Textarea rows={2} value={data[`${k}_description`] || ''} onChange={e => setData({ ...data, [`${k}_description`]: e.target.value })} /></div>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} className="btn-brand shadow-lg"><Save className="w-4 h-4" /> Save All Changes</button>
      </div>
    </div>
  );
}
